import { useState, useMemo } from 'react';
import { Download } from 'lucide-react';
import {
  QUICK_PERIODS, getQuickRange, exportCSV,
} from '../../utils/reportHelpers';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d} ${MONTHS[parseInt(m,10)-1]} ${y}`;
}

function fmtDays(n, dp = 1) {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return dp === 0 ? `${Math.round(n)}d` : `${n.toFixed(dp)}d`;
}

function dayColor(days) {
  if (days === null || days === undefined) return '#475569';
  if (days <= 1)  return '#16a34a';
  if (days <= 3)  return '#475569';
  if (days <= 7)  return '#d97706';
  return '#dc2626';
}

/**
 * For each trip, find status_change amendments: submission = to:'pending_approval',
 * next actioning = from:'pending_approval'. Returns the most recent pair only.
 */
function extractTurnaround(trip) {
  const amends = (trip.amendments || [])
    .filter(a => a.type === 'status_change' && a.at)
    .sort((a, b) => (a.at < b.at ? -1 : 1));

  let result = null;

  for (let i = 0; i < amends.length; i++) {
    const a = amends[i];
    if (a.to !== 'pending_approval') continue;
    // Find next actioning after this submission
    for (let j = i + 1; j < amends.length; j++) {
      const b = amends[j];
      if (b.from !== 'pending_approval') continue;
      const submittedAt = new Date(a.at);
      const actionedAt  = new Date(b.at);
      const days = (actionedAt - submittedAt) / 86400000;
      if (days < 0) continue;
      result = {
        tripId:          trip.id,
        tripTitle:       trip.title || trip.tripRef || trip.id,
        tripRef:         trip.tripRef || trip.id,
        tripType:        trip.tripType || '',
        travellerName:   trip.travellerName || '',
        submittedAt:     a.at,
        submittedDate:   a.at.slice(0, 10),
        actionedAt:      b.at,
        actionedDate:    b.at.slice(0, 10),
        approverName:    b.byName || '—',
        outcome:         b.to || '',
        days,
      };
      i = j; // skip to after this pair for next iteration
      break;
    }
  }

  return result;
}

export default function ApprovalTurnaroundReport({ trips }) {
  const [periodKey, setPeriodKey] = useState('allTime');
  const [from,      setFrom]      = useState('2000-01-01');
  const [to,        setTo]        = useState('2099-12-31');
  const [typeFilter, setTypeFilter] = useState([]);

  const applyPreset = (key) => {
    setPeriodKey(key);
    if (key === 'custom') return;
    const r = getQuickRange(key);
    setFrom(r.from);
    setTo(r.to);
  };

  const toggleType = (t) =>
    setTypeFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  // Extract all turnaround records
  const allRecords = useMemo(() => {
    const records = [];
    trips.forEach(trip => {
      const r = extractTurnaround(trip);
      if (r) records.push(r);
    });
    return records;
  }, [trips]);

  // Trip types present in data (use actual values, not hardcoded list)
  const presentTypes = useMemo(() => {
    return [...new Set(allRecords.map(r => r.tripType).filter(Boolean))].sort();
  }, [allRecords]);

  // Filtered by date (actionedDate) and trip type
  const filtered = useMemo(() => {
    return allRecords.filter(r => {
      if (from && r.actionedDate < from) return false;
      if (to   && r.actionedDate > to)   return false;
      if (typeFilter.length && !typeFilter.includes(r.tripType)) return false;
      return true;
    });
  }, [allRecords, from, to, typeFilter]);

  // Summary stats
  const summary = useMemo(() => {
    if (!filtered.length) return null;
    const days = filtered.map(r => r.days);
    const avg     = days.reduce((s, d) => s + d, 0) / days.length;
    const fastest = Math.min(...days);
    const slowest = Math.max(...days);
    const within1 = days.filter(d => d <= 1).length;
    return { avg, fastest, slowest, pctWithin1: (within1 / days.length) * 100 };
  }, [filtered]);

  // By approver
  const byApprover = useMemo(() => {
    const groups = {};
    filtered.forEach(r => {
      const key = r.approverName || '—';
      if (!groups[key]) groups[key] = [];
      groups[key].push(r.days);
    });
    return Object.entries(groups)
      .map(([approver, daysList]) => ({
        approver,
        count:  daysList.length,
        avg:    daysList.reduce((s, d) => s + d, 0) / daysList.length,
        min:    Math.min(...daysList),
        max:    Math.max(...daysList),
      }))
      .sort((a, b) => a.avg - b.avg);
  }, [filtered]);

  // By trip type
  const byType = useMemo(() => {
    const groups = {};
    filtered.forEach(r => {
      const key = r.tripType || 'Unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push(r.days);
    });
    return Object.entries(groups)
      .map(([type, daysList]) => ({
        type,
        count: daysList.length,
        avg:   daysList.reduce((s, d) => s + d, 0) / daysList.length,
      }))
      .sort((a, b) => a.avg - b.avg);
  }, [filtered]);

  // Detail table sorted chronologically by actionedAt desc
  const detail = useMemo(() =>
    [...filtered].sort((a, b) => (b.actionedAt < a.actionedAt ? -1 : 1)),
  [filtered]);

  const handleExport = () => {
    const headers = ['Trip Ref', 'Trip', 'Type', 'Traveller', 'Submitted', 'Actioned', 'Approver', 'Outcome', 'Days'];
    const rows = detail.map(r => [
      r.tripRef,
      r.tripTitle,
      r.tripType,
      r.travellerName,
      r.submittedDate,
      r.actionedDate,
      r.approverName,
      r.outcome,
      r.days.toFixed(1),
    ]);
    exportCSV([headers, ...rows], `approval_turnaround_${from}_to_${to}.csv`);
  };

  return (
    <div style={{ fontFamily: "'DM Sans','Helvetica Neue',sans-serif" }}>

      {/* Filters */}
      <div style={card}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:14, marginBottom:14 }}>
          <div>
            <label style={lbl}>Period</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {QUICK_PERIODS.map(p => (
                <button key={p.key} onClick={() => applyPreset(p.key)}
                  style={{ ...pill, ...(periodKey === p.key ? pillActive : pillInactive) }}>
                  {p.label}
                </button>
              ))}
              <button onClick={() => applyPreset('custom')}
                style={{ ...pill, ...(periodKey === 'custom' ? pillActive : pillInactive) }}>
                Custom
              </button>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:8 }}>
              <div>
                <label style={lbl}>From</label>
                <input type="date" value={from}
                  onChange={e => { setFrom(e.target.value); setPeriodKey('custom'); }}
                  style={inp} />
              </div>
              <div>
                <label style={lbl}>To</label>
                <input type="date" value={to}
                  onChange={e => { setTo(e.target.value); setPeriodKey('custom'); }}
                  style={inp} />
              </div>
            </div>
          </div>
        </div>

        {presentTypes.length > 1 && (
          <div>
            <label style={lbl}>Trip Type</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {presentTypes.map(t => (
                <button key={t} onClick={() => toggleType(t)}
                  style={{ ...pill, ...(typeFilter.includes(t) ? pillActive : pillInactive) }}>
                  {typeFilter.includes(t) ? '✓ ' : ''}{t}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div style={{ ...card, textAlign:'center', padding:'60px 20px' }}>
          <div style={{ fontSize:13, color:'#475569', marginBottom:6 }}>
            {allRecords.length === 0
              ? 'No approval workflow data found. This report only includes trips that have gone through pending approval — trips approved directly without the approval step will not appear.'
              : 'No completed approval workflows match the current filters.'}
          </div>
          {allRecords.length > 0 && (
            <div style={{ fontSize:12, color:'#64748b' }}>
              {allRecords.length} workflow{allRecords.length !== 1 ? 's' : ''} found across all time — try widening the date range.
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
            <div style={summaryCard}>
              <div style={summaryLabel}>Avg Turnaround</div>
              <div style={summaryValue}>{fmtDays(summary.avg)}</div>
            </div>
            <div style={summaryCard}>
              <div style={summaryLabel}>Fastest</div>
              <div style={{ ...summaryValue, color:'#16a34a' }}>{fmtDays(summary.fastest)}</div>
            </div>
            <div style={summaryCard}>
              <div style={summaryLabel}>Slowest</div>
              <div style={{ ...summaryValue, color:'#dc2626' }}>{fmtDays(summary.slowest)}</div>
            </div>
            <div style={summaryCard}>
              <div style={summaryLabel}>Actioned within 1 day</div>
              <div style={summaryValue}>{summary.pctWithin1.toFixed(0)}%</div>
            </div>
          </div>

          {/* Two-column breakdown tables */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>

            {/* By approver */}
            <div style={card}>
              <div style={sectionHeading}>By Approver</div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
                      <th scope="col" style={th}>Approver</th>
                      <th scope="col" style={{ ...th, textAlign:'right' }}>Count</th>
                      <th scope="col" style={{ ...th, textAlign:'right' }}>Avg days</th>
                      <th scope="col" style={{ ...th, textAlign:'right' }}>Min</th>
                      <th scope="col" style={{ ...th, textAlign:'right' }}>Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byApprover.map((row, idx) => (
                      <tr key={row.approver} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa', borderBottom:'1px solid #f1f5f9' }}>
                        <td style={{ ...td, fontWeight:500, color:'#1e293b' }}>{row.approver}</td>
                        <td style={{ ...td, textAlign:'right', color:'#475569' }}>{row.count}</td>
                        <td style={{ ...td, textAlign:'right', fontWeight:600, color: dayColor(row.avg) }}>{fmtDays(row.avg)}</td>
                        <td style={{ ...td, textAlign:'right', color:'#16a34a' }}>{fmtDays(row.min)}</td>
                        <td style={{ ...td, textAlign:'right', color:'#dc2626' }}>{fmtDays(row.max)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* By trip type */}
            <div style={card}>
              <div style={sectionHeading}>By Trip Type</div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
                      <th scope="col" style={th}>Type</th>
                      <th scope="col" style={{ ...th, textAlign:'right' }}>Count</th>
                      <th scope="col" style={{ ...th, textAlign:'right' }}>Avg days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byType.map((row, idx) => (
                      <tr key={row.type} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa', borderBottom:'1px solid #f1f5f9' }}>
                        <td style={{ ...td, fontWeight:500, color:'#1e293b' }}>{row.type}</td>
                        <td style={{ ...td, textAlign:'right', color:'#475569' }}>{row.count}</td>
                        <td style={{ ...td, textAlign:'right', fontWeight:600, color: dayColor(row.avg) }}>{fmtDays(row.avg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Detail table */}
          <div style={card}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <div style={sectionHeading}>All Approval Workflows</div>
              <button onClick={handleExport}
                aria-label="Export approval turnaround data as CSV"
                style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', background:'#0d9488', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                <Download size={14} aria-hidden="true" />
                Export CSV
              </button>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
                    <th scope="col" style={th}>Trip</th>
                    <th scope="col" style={th}>Type</th>
                    <th scope="col" style={th}>Submitted</th>
                    <th scope="col" style={th}>Actioned</th>
                    <th scope="col" style={th}>Approver</th>
                    <th scope="col" style={th}>Outcome</th>
                    <th scope="col" style={{ ...th, textAlign:'right' }}>Days</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.map((r, idx) => (
                    <tr key={`${r.tripId}-${r.actionedAt}`}
                      style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa', borderBottom:'1px solid #f1f5f9' }}>
                      <td style={td}>
                        <div style={{ fontWeight:500, color:'#1e293b' }}>{r.tripTitle}</div>
                        {r.travellerName && (
                          <div style={{ fontSize:11, color:'#475569', marginTop:1 }}>{r.travellerName}</div>
                        )}
                      </td>
                      <td style={{ ...td, color:'#475569', whiteSpace:'nowrap' }}>{r.tripType || '—'}</td>
                      <td style={{ ...td, color:'#475569', whiteSpace:'nowrap' }}>{fmtDate(r.submittedDate)}</td>
                      <td style={{ ...td, color:'#475569', whiteSpace:'nowrap' }}>{fmtDate(r.actionedDate)}</td>
                      <td style={{ ...td, color:'#475569' }}>{r.approverName}</td>
                      <td style={{ ...td, whiteSpace:'nowrap' }}>
                        {r.outcome === 'approved' && (
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:'#f0fdf4', color:'#16a34a', border:'1px solid #bbf7d0' }}>
                            Approved
                          </span>
                        )}
                        {r.outcome === 'declined' && (
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca' }}>
                            Declined
                          </span>
                        )}
                        {r.outcome !== 'approved' && r.outcome !== 'declined' && (
                          <span style={{ fontSize:11, color:'#475569' }}>{r.outcome || '—'}</span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign:'right', fontWeight:700, color: dayColor(r.days), whiteSpace:'nowrap' }}>
                        {fmtDays(r.days)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p style={{ fontSize:11, color:'#475569', marginTop:12, textAlign:'center' }}>
            Filtered by date the trip was actioned · Days = time from submission to approval/decline
            · <span style={{ color:'#16a34a', fontWeight:600 }}>Green</span> ≤ 1 day
            · <span style={{ color:'#475569', fontWeight:600 }}>Gray</span> ≤ 3 days
            · <span style={{ color:'#d97706', fontWeight:600 }}>Amber</span> ≤ 7 days
            · <span style={{ color:'#dc2626', fontWeight:600 }}>Red</span> &gt; 7 days
          </p>
        </>
      )}
    </div>
  );
}

// Style constants
const card         = { background:'#fff', borderRadius:12, border:'1px solid #e2e8f0', padding:'18px 20px', marginBottom:20, boxShadow:'0 1px 3px rgba(0,0,0,0.05)' };
const lbl          = { display:'block', fontSize:10, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 };
const inp          = { padding:'7px 10px', border:'1px solid #cbd5e1', borderRadius:8, fontSize:13, color:'#1e293b', outline:'none', background:'#fff' };
const pill         = { padding:'5px 10px', border:'1px solid', borderRadius:6, fontSize:12, fontWeight:500, cursor:'pointer', transition:'all 0.15s' };
const pillActive   = { background:'#0d9488', color:'#fff', borderColor:'#0d9488' };
const pillInactive = { background:'#f1f5f9', color:'#475569', borderColor:'#e2e8f0' };
const summaryCard  = { background:'#fff', borderRadius:10, border:'1px solid #e2e8f0', padding:'14px 18px', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' };
const summaryLabel = { fontSize:10, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 };
const summaryValue = { fontSize:22, fontWeight:800, color:'#0f172a', lineHeight:1.2 };
const sectionHeading = { fontSize:14, fontWeight:600, color:'#374151', marginBottom:12 };
const th = { padding:'10px 12px', textAlign:'left', fontSize:10, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' };
const td = { padding:'10px 12px', color:'#374151', verticalAlign:'middle' };
